import { useMemo } from "react";

/**
 * 표 검색 공통 유틸 — <b>시스템 전체가 이 한 구현만 쓴다.</b>
 *
 * <h3>왜 유틸로 뺐나</h3>
 * <p>예전엔 표마다 자기 필터 함수를 들고 있었다. 어떤 표는 대소문자를 무시하고 어떤 표는 안 하고,
 * 어떤 표는 공백을 다듬고 어떤 표는 안 다듬고, 검색 대상 컬럼도 손으로 나열해 관리했다.
 * 그래서 "이 표에선 검색되는데 저 표에선 안 되네" 가 반복됐다. 규칙을 여기 한 곳에 둔다.
 *
 * <h3>기본 동작 — 컬럼을 지정하지 않는다</h3>
 * <p>표 안에서 훑는 검색이므로 <b>행의 모든 값</b>을 대상으로 한다. 표마다 검색 필드를 나열·관리할
 * 필요가 없고, 나중에 컬럼이 늘어도 검색이 저절로 따라온다. 중첩 객체({@code vendor: {name}})도
 * 펼쳐서 보는데, 화면에 보이는 글자가 대개 그 안에 들어 있기 때문이다.
 *
 * <p>대신 id·타임스탬프 같은 내부 값도 걸릴 수 있다. 그게 문제가 되는 표만 {@code fields} 로 좁힌다.
 */

/** 순환 참조·거대 객체 방어용 — 이 깊이까지만 펼친다. */
const MAX_DEPTH = 3;

/** 값(스칼라/객체/배열)에서 검색 대상 문자열만 모은다. */
function collectStrings(value, depth, out) {
    if (value == null || depth > MAX_DEPTH) return;
    if (typeof value === "object") {
        for (const v of Object.values(value)) collectStrings(v, depth + 1, out);
        return;
    }
    if (typeof value === "function") return;
    out.push(String(value).toLowerCase());
}

/** 검색어 정규화 — 앞뒤 공백 제거 + 소문자. 빈 문자열이면 "검색 안 함". */
export function normalizeQuery(query) {
    return String(query ?? "").trim().toLowerCase();
}

/**
 * 행 하나가 검색어에 걸리는지.
 * @param {object|any} row     검사할 행
 * @param {string} query       검색어 (비면 항상 true)
 * @param {string[]} [fields]  검사할 필드명. 생략하면 행 전체를 훑는다.
 */
export function matchesQuery(row, query, fields) {
    const q = normalizeQuery(query);
    if (!q) return true;
    const out = [];
    if (fields && fields.length) {
        for (const f of fields) collectStrings(row?.[f], 0, out);
    } else {
        collectStrings(row, 0, out);
    }
    return out.some(s => s.includes(q));
}

/**
 * 값 목록이 검색어에 걸리는지 — 레거시 {@code searchMatch(query, [값들])} 형태와 같은 시그니처.
 * <p>새 코드는 {@link matchesQuery} 나 {@link useTableSearch} 를 쓰고, 이건 기존 호출부 호환용이다.
 */
export function matchesValues(query, values) {
    const q = normalizeQuery(query);
    if (!q) return true;
    const out = [];
    for (const v of values || []) collectStrings(v, 0, out);
    return out.some(s => s.includes(q));
}

/**
 * 메모리 배열 검색 훅 — 표가 전체 데이터를 들고 있을 때 한 줄로 끝낸다.
 *
 * <pre>
 *   const [q, setQ] = useState("");
 *   const rows = useTableSearch(allRows, q);          // 컬럼 지정 불필요
 *   &lt;TableToolbar onSearch={setQ} /&gt;
 * </pre>
 *
 * <p>서버에서 걸러야 하는 표(전체를 안 들고 있는 경우)는 이 훅 대신 검색어만 받아 직접 조회한다.
 */
export function useTableSearch(rows, query, fields) {
    return useMemo(() => {
        const q = normalizeQuery(query);
        if (!q) return rows || [];
        return (rows || []).filter(r => matchesQuery(r, q, fields));
    }, [rows, query, fields]);
}
